import type { ResultOf } from '@graphql-typed-document-node/core';
import type { EditOpportunityDialogClientFragment } from 'src/components/advisor-dashboard/opportunities/edit-opportunity/client-opportunity/edit-opportunity-fragments';
import type { FragmentType } from 'src/gql';
import type { SalesforceAccount } from 'src/types/salesforce-account';
import { useMemo } from 'react';
import { isNil } from 'es-toolkit';
import { graphql } from 'src/gql';
import { createAccountAdapter } from 'src/utils/salesforce/account';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

// Own document, not a field on HouseholdAccountFragment/ADVISOR_DASH_CLIENT_QUERY: both are shared, so
// widening either changes the payload of all 19 household pages.
export const HOUSEHOLD_HEADER_CREATE_DATA_QUERY = graphql(/* GraphQL */ `
  query HouseholdHeaderCreateData($householdId: ID!) {
    household(id: $householdId) {
      id
      name
      salesforceAccountId
      advisor {
        id
        salesforceUserId
        user {
          id
          fullName
        }
        centerOfInfluenceReps {
          id
          name
        }
      }
      incomingProfessionalRelationships {
        id
        relationshipType
        role
        source {
          __typename
          ... on CenterOfInfluenceRep {
            id
          }
        }
      }
      primaryClient {
        id
        salesforceContactId
        user {
          id
          firstName
          lastName
          email
          phoneNumber
          profilePicUrl
        }
      }
      coClient {
        id
        salesforceContactId
        user {
          id
          firstName
          lastName
          email
          phoneNumber
          profilePicUrl
        }
      }
      ...EditOpportunityDialogClient
    }
  }
`);

export type OpportunityHouseholdFragment = FragmentType<typeof EditOpportunityDialogClientFragment>;

/**
 * The raw household alongside the adapted account. `createAccountAdapter` reshapes into the legacy
 * Salesforce shape, which drops the household name and the client users the contact dialog needs.
 * Derived from the query so narrowing the selection set is a type error rather than a silent lie.
 */
export type HouseholdHeaderCreateHousehold = Pick<
  NonNullable<ResultOf<typeof HOUSEHOLD_HEADER_CREATE_DATA_QUERY>['household']>,
  'name' | 'primaryClient' | 'coClient' | 'advisor' | 'incomingProfessionalRelationships'
>;

export type HouseholdHeaderCreateData =
  | {
      readonly account: null;
      readonly household: null;
      readonly opportunityHouseholdFrag: null;
      readonly refetch: () => void;
      readonly status: 'loading';
    }
  | {
      readonly account: SalesforceAccount;
      readonly household: HouseholdHeaderCreateHousehold;
      readonly opportunityHouseholdFrag: OpportunityHouseholdFragment;
      readonly refetch: () => void;
      readonly status: 'noContacts';
    }
  | {
      readonly account: SalesforceAccount;
      readonly household: HouseholdHeaderCreateHousehold;
      readonly opportunityHouseholdFrag: OpportunityHouseholdFragment;
      readonly refetch: () => void;
      readonly status: 'ready';
    };

type UseHouseholdHeaderCreateDataArgs = {
  readonly householdId: string | null | undefined;
  readonly isHouseholdPageRevampEnabled: boolean;
};

export function useHouseholdHeaderCreateData({
  householdId,
  isHouseholdPageRevampEnabled,
}: UseHouseholdHeaderCreateDataArgs): HouseholdHeaderCreateData {
  const hasHouseholdId = !isNil(householdId) && householdId !== '';

  const { data, error, refetch } = useQueryWithErrorLogging(HOUSEHOLD_HEADER_CREATE_DATA_QUERY, {
    variables: { householdId: householdId ?? '' },
    skip: !isHouseholdPageRevampEnabled || !hasHouseholdId,
  });

  const household = data?.household;

  return useMemo<HouseholdHeaderCreateData>(() => {
    // Check error first: after a failed request Apollo keeps handing back the last successful
    // result's data, so skipping this would leave a stale account on screen as if it were fresh.
    // salesforceAccountId is checked here because createAccountAdapter non-null-asserts it into
    // account.Id, which consumers interpolate straight into routes.
    if (!isNil(error) || isNil(household) || isNil(household.salesforceAccountId)) {
      return { account: null, household: null, opportunityHouseholdFrag: null, refetch, status: 'loading' };
    }

    const account = createAccountAdapter(
      household,
      household.advisor?.user.fullName,
      household.advisor?.salesforceUserId,
    );

    // A safety net, not a routine state: Rails serves each record's uuid as its salesforceContactId
    // unless the replace-salesforce-id-with-uuid flag is off or LaunchDarkly is unreachable.
    const records = account.Contacts?.records;
    return isNil(records) || records.length === 0
      ? { account, household, opportunityHouseholdFrag: household, refetch, status: 'noContacts' }
      : { account, household, opportunityHouseholdFrag: household, refetch, status: 'ready' };
  }, [error, household, refetch]);
}
